import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const passwordStrength = (pwd) => {
  let score = 0;
  if (pwd.length >= 8) score++;
  if (pwd.length >= 12) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  if (score <= 1) return { label: 'Weak', color: 'var(--danger)', width: '20%' };
  if (score <= 2) return { label: 'Fair', color: 'var(--warn)', width: '40%' };
  if (score <= 3) return { label: 'Good', color: 'var(--accent)', width: '65%' };
  return { label: 'Strong', color: 'var(--success)', width: '100%' };
};

export default function SetPassword() {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);
  const [message, setMessage] = useState('');
  const [fromSession, setFromSession] = useState(false); // arrived signed in, not via an email link
  const navigate = useNavigate();
  const strength = passwordStrength(password);

  useEffect(() => {
    // Supabase puts the token in the URL hash after a redirect
    // We need to let Supabase process it via onAuthStateChange
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setReady(true);
      } else if (event === 'SIGNED_IN' && window.location.hash.includes('type=recovery')) {
        setReady(true);
      } else if (event === 'SIGNED_IN' && window.location.hash.includes('type=invite')) {
        setReady(true);
      }
    });

    // Also check if we already have a session from the hash
    const hash = window.location.hash;
    if (hash && hash.includes('access_token')) {
      const params = new URLSearchParams(hash.substring(1));
      const type = params.get('type');
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token') || '';

      if (accessToken && (type === 'recovery' || type === 'invite' || type === 'signup')) {
        supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
          .then(({ error: err }) => {
            if (err) setMessage('This link has expired. Please request a new one.');
            else {
              setReady(true);
              window.history.replaceState(null, '', window.location.pathname);
            }
          });
      } else if (accessToken) {
        supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
          .then(({ error: err }) => {
            if (err) setMessage('This link has expired. Please request a new one.');
            else {
              setReady(true);
              window.history.replaceState(null, '', window.location.pathname);
            }
          });
      } else {
        setMessage('Invalid reset link. Please request a new password reset.');
      }
    } else {
      // No hash — check for existing session
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) { setReady(true); setFromSession(true); }
        else setMessage('No reset token found. Please request a new password reset.');
      });
    }

    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (!/[A-Z]/.test(password)) { setError('Password must contain at least one uppercase letter.'); return; }
    if (!/[0-9]/.test(password)) { setError('Password must contain at least one number.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    const { error: err } = await supabase.auth.updateUser({ password });
    if (err) { setError(err.message); setLoading(false); return; }
    navigate('/dashboard');
  };

  return (
    <div style={{ minHeight:'100vh', background:'var(--navy)', display:'flex', alignItems:'center', justifyContent:'center', padding:20, fontFamily:'DM Sans, sans-serif' }}>
      <div style={{ width:'100%', maxWidth:420 }}>
        <div style={{ textAlign:'center', marginBottom:32 }}>
          <img src="/logo.jpg" alt="A Heart For Care" style={{ width:120, height:120, borderRadius:16, objectFit:'cover', marginBottom:16, boxShadow:'0 4px 24px rgba(0,0,0,0.3)' }} />
          <p style={{ color:'rgba(255,255,255,0.5)', fontSize:13 }}>Set your password to continue</p>
        </div>
        <div style={{ background:'#fff', borderRadius:12, padding:32 }}>
          {message ? (
            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:32, marginBottom:12 }}>⚠</div>
              <div style={{ fontSize:14, color:'var(--danger)', marginBottom:8, fontWeight:500 }}>Link expired or invalid</div>
              <div style={{ fontSize:13, color:'var(--text3)', marginBottom:20 }}>{message}</div>
              <button className="btn btn-primary btn-full" onClick={() => navigate('/login')}>Back to Login</button>
            </div>
          ) : !ready ? (
            <div style={{ textAlign:'center', padding:20 }}>
              <div className="loading-spinner" style={{ margin:'0 auto 12px', width:24, height:24 }} />
              <p style={{ fontSize:13, color:'var(--text3)' }}>Verifying your link...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:15, fontWeight:600, color:'var(--text)', marginBottom:4 }}>Set your password</div>
                <div style={{ fontSize:13, color:'var(--text3)' }}>Min. 8 characters, one uppercase letter and one number.</div>
              </div>
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input type="password" className="form-input" value={password} onChange={e => setPassword(e.target.value)} placeholder="Minimum 8 characters" required autoFocus />
                {password && (
                  <div style={{ marginTop:8 }}>
                    <div style={{ background:'var(--border)', borderRadius:4, height:4 }}>
                      <div style={{ background:strength.color, height:4, borderRadius:4, width:strength.width, transition:'width 0.3s' }} />
                    </div>
                    <div style={{ fontSize:11, color:strength.color, marginTop:4, fontWeight:500 }}>{strength.label}</div>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Confirm Password</label>
                <input type="password" className="form-input" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat your password" required />
                {confirm && password !== confirm && <div style={{ fontSize:11, color:'var(--danger)', marginTop:4 }}>Passwords do not match</div>}
                {confirm && password === confirm && <div style={{ fontSize:11, color:'var(--success)', marginTop:4 }}>✓ Passwords match</div>}
              </div>
              {error && (
                <div style={{ background:'var(--danger-bg)', border:'1px solid var(--danger-bg)', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:13, color:'var(--danger)' }}>{error}</div>
              )}
              <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={loading || password !== confirm || password.length < 8}>
                {loading ? <span className="loading-spinner" style={{ borderTopColor:'#fff' }} /> : null}
                {fromSession ? 'Update Password' : 'Set Password & Sign In'}
              </button>
              {fromSession && (
                <button type="button" className="btn btn-ghost btn-full" style={{ marginTop:10 }} onClick={() => navigate(-1)}>Cancel</button>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
